import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faUniversity,
  faCheck,
  faLock,
  faPaperPlane,
  faUser,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import OtpInput from "react-otp-input";
import CustomTypeahead from "../../components/CustomTypeahead";
import { createTokenPin, fetchApi, postApi } from "../../../redux/api";
import { accountTypes } from "../../accounts/AccountForm";
import useBankList from "../../../hooks/useBankList";
import { Typeahead } from "react-bootstrap-typeahead";
import CustomButton from "../../../components/CustomButton";
import { formatNumber } from "../../../redux/utils";
import toast from "react-hot-toast";
import BackButton from "../../../components/BackButton";
import { useNavigate } from "react-router-dom";
import "./DirectTransfer.css";

function DirectTransfer() {
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalIsOpen1, setModalIsOpen1] = useState(false);
  const openConfirmation = () => setModalIsOpen1(true);
  const closeConfirmation = () => {
    setLoading(false);
    setPin("");
    setModalIsOpen1(false);
  };
  const history = useNavigate();
  const [form, setForm] = useState({
    ben_dec: null,
    ben_acc: "",
    ben_bank: "",
    account_number: null,
    account_type: null,
    bank_code: null,
    amount: "",
    account_name: null,
    type: null,
    title: null,
    description: "",
    beneficiary_name: "",
    ben_acc_type: "Default",
    id: null,
  });
  const [pin, setPin] = useState("");
  const [paymentProvider, setPaymentProvider] = useState("interswitch");
  const [lookupLoading, setLookupLoading] = useState(false);
  const { bankList } = useBankList();

  useEffect(() => {
    setIsLoading(true);
    fetchApi("pan_details/get")
      .then((resp) => {
        if (resp?.data) setList(resp.data);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const accTypes = accountTypes.map((item) => item.title);

  const checklist = useMemo(
    () => [
      { key: "source", label: "Source account selected", done: Boolean(form.id) },
      {
        key: "beneficiary",
        label: "Beneficiary details complete",
        done:
          Boolean(form.ben_bank) &&
          Boolean(form.ben_acc?.length >= 10) &&
          Boolean(form.beneficiary_name) &&
          Boolean(form.ben_acc_type),
      },
      {
        key: "payment",
        label: "Amount & description entered",
        done: Boolean(form.amount) && Number(form.amount) > 0 && Boolean(form.description),
      },
    ],
    [form]
  );

  const formComplete = checklist.every((item) => item.done);

  const resolveBeneficiaryBankCode = () =>
    bankList.find((item) => item.title === form.ben_bank)?.code || "";

  const lookupBeneficiaryName = async () => {
    if (paymentProvider !== "remita") return;
    const bankCode = resolveBeneficiaryBankCode();
    if (!bankCode || form.ben_acc.length < 10) return;

    setLookupLoading(true);
    try {
      const res = await postApi("remita/account-lookup", {
        bankCode,
        accountNumber: form.ben_acc,
      });
      if (res?.success && res.accountName) {
        setForm((prev) => ({
          ...prev,
          beneficiary_name: res.accountName,
        }));
        toast.success("Account name verified via Remita");
      } else {
        toast.error(res?.message || "Could not verify account name");
      }
    } catch (err) {
      toast.error(err?.message || "Account lookup failed");
    } finally {
      setLookupLoading(false);
    }
  };

  const validateForm = () => {
    if (!formComplete) {
      toast.error("Please complete all required fields before sending.");
      return false;
    }
    return true;
  };

  const processPayment = async () => {
    if (pin.length < 4) {
      toast.error("Enter your 4-digit PIN to confirm.");
      return;
    }
    setLoading(true);
    const finalList = [];
    const new_amount = form.amount * 100;
    const input_amount = new_amount?.toString().split(".")[0];
    finalList.push({
      amount: input_amount,
      narration: form.description.replace("'", ""),
      beneficiary_code: "Direct0001",
      ben_email: "",
      ben_bank_cbn_code:
        bankList.find((i) => i.title === form.ben_bank)?.code || "044",
      ben_account_no: form.ben_acc,
      account_type:
        accountTypes.find((i) => i.title === form.ben_acc_type)?.code || "10",
      is_prepaid_load: "FALSE",
      currency: "NGN",
      beneficiary_name: form.beneficiary_name?.replace("'", ""),
      beneficiary_mobile_no: "",
      type: 1,
    });

    try {
      const transferEndpoint =
        paymentProvider === "remita"
          ? "remita/single-transfer"
          : "autogate/single-transfer";

      const transferForm = {
        ...form,
        ben_bank_cbn_code: resolveBeneficiaryBankCode(),
      };

      const res = await postApi(transferEndpoint, {
        details: finalList,
        description: "Payment",
        id: form.id,
        vPinToken: await createTokenPin({ vPinToken: pin }),
        form: transferForm,
        paymentProvider,
      });
      if (res.success) {
        closeConfirmation();
        toast.success(res.message || "Transfer initiated successfully");
        history(-1);
      } else {
        toast.error(res.message || "Transfer failed");
        setLoading(false);
      }
    } catch (err) {
      toast.error(err?.message || "Transfer failed");
      setLoading(false);
    }
  };

  return (
    <div className="dt-page">
      <BackButton />
      <div className="dt-header">
        <h4>Direct Transfer</h4>
        <p>Send funds instantly from a government account to any beneficiary bank.</p>
        <div className="dt-provider-toggle" role="group" aria-label="Payment provider">
          <button
            type="button"
            className={paymentProvider === "interswitch" ? "active" : ""}
            onClick={() => setPaymentProvider("interswitch")}
          >
            Interswitch (SFTP)
          </button>
          <button
            type="button"
            className={paymentProvider === "remita" ? "active" : ""}
            onClick={() => setPaymentProvider("remita")}
          >
            Remita
          </button>
        </div>
        {paymentProvider === "remita" && (
          <p className="dt-provider-note">
            Remita debits your prefunded government account — beneficiary name is verified automatically.
          </p>
        )}
      </div>

      <Row className="g-4">
        <Col lg={7}>
          <Card className="dt-form-card">
            <CardBody>
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="success" />
                  <p className="text-muted mt-2 mb-0">Loading accounts…</p>
                </div>
              ) : (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (validateForm()) openConfirmation();
                  }}
                >
                  <div className="dt-section">
                    <div className="dt-section-title">
                      <span className="dt-step">1</span>
                      <FontAwesomeIcon icon={faWallet} />
                      Source account
                    </div>
                    <CustomTypeahead
                      options={list}
                      labelKey="account_name"
                      renderMenuItemChildren={(item) => {
                        const selected = bankList.find(
                          (i) => i.code === item.bank_code
                        );
                        const accountType = accountTypes.find(
                          (i) => i.code === item.account_type
                        );
                        return (
                          <>
                            <div className="fw-semibold">{item.account_name}</div>
                            <div className="text-muted small">
                              {selected?.title} ({item.account_number})
                            </div>
                            <div className="text-muted small">
                              {item.type} · {accountType?.title}
                            </div>
                          </>
                        );
                      }}
                      onChange={(s) => {
                        if (s.length) {
                          const selected = bankList.find(
                            (i) => i.code === s[0].bank_code
                          );
                          setForm((p) => ({
                            ...p,
                            id: s[0].id,
                            account_number: s[0].account_number,
                            account_type: s[0].account_type,
                            bank_code: s[0].bank_code,
                            account_name: s[0].account_name,
                            title: selected?.title,
                            type: s[0].type,
                          }));
                        }
                      }}
                      label={
                        <span>
                          Choose account <span className="text-danger">*</span>
                        </span>
                      }
                    />
                  </div>

                  <div className="dt-section">
                    <div className="dt-section-title">
                      <span className="dt-step">2</span>
                      <FontAwesomeIcon icon={faUser} />
                      Beneficiary
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Beneficiary bank <span className="text-danger">*</span>
                      </Form.Label>
                      <Typeahead
                        id="ben_bank"
                        labelKey="title"
                        options={bankList}
                        placeholder="Search bank…"
                        selected={bankList.filter(
                          (item) => item.title === form.ben_bank
                        )}
                        onChange={(selected) => {
                          const value =
                            selected.length > 0 ? selected[0].title : "";
                          setForm((p) => ({ ...p, ben_bank: value }));
                        }}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Account number <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            type="text"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="10-digit NUBAN"
                            value={form.ben_acc}
                            onChange={({ target: { value } }) =>
                              setForm((p) => ({
                                ...p,
                                ben_acc: value.replace(/\D/g, ""),
                              }))
                            }
                            onBlur={lookupBeneficiaryName}
                          />
                          {paymentProvider === "remita" && lookupLoading && (
                            <Form.Text className="text-muted">
                              Verifying account name with Remita…
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Account type <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={form.ben_acc_type}
                            required
                            onChange={({ target: { value } }) =>
                              setForm((p) => ({ ...p, ben_acc_type: value }))
                            }
                          >
                            <option value="">Select type</option>
                            {accTypes.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-0">
                      <Form.Label>
                        Beneficiary name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        placeholder="Name as it appears on the account"
                        value={form.beneficiary_name}
                        onChange={({ target: { value } }) =>
                          setForm((p) => ({ ...p, beneficiary_name: value }))
                        }
                      />
                    </Form.Group>
                  </div>

                  <div className="dt-section">
                    <div className="dt-section-title">
                      <span className="dt-step">3</span>
                      <FontAwesomeIcon icon={faUniversity} />
                      Payment details
                    </div>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Amount{" "}
                            {form.amount ? (
                              <span className="text-success">
                                (₦{formatNumber(form.amount)})
                              </span>
                            ) : null}{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            type="number"
                            min="1"
                            step="0.01"
                            placeholder="0.00"
                            value={form.amount}
                            onChange={({ target: { value } }) =>
                              setForm((p) => ({ ...p, amount: value }))
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Description <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            placeholder="Payment narration"
                            value={form.description}
                            onChange={({ target: { value } }) =>
                              setForm((p) => ({ ...p, description: value }))
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="dt-summary-card">
            <CardBody>
              <h6 className="fw-bold text-success mb-3">Transfer preview</h6>
              <div className="dt-provider-badge">
                Via {paymentProvider === "remita" ? "Remita" : "Interswitch SFTP"}
              </div>

              <div className="dt-flow">
                <div className={`dt-flow-box ${form.account_name ? "active" : "empty"}`}>
                  {form.account_name ? (
                    <>
                      <div className="dt-summary-label">From</div>
                      <div className="fw-semibold small">{form.account_name}</div>
                      <div className="text-muted small">
                        {form.title} · {form.account_number}
                      </div>
                    </>
                  ) : (
                    "Select source account"
                  )}
                </div>
                <div className="dt-flow-arrow">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
                <div
                  className={`dt-flow-box ${
                    form.beneficiary_name ? "active" : "empty"
                  }`}
                >
                  {form.beneficiary_name ? (
                    <>
                      <div className="dt-summary-label">To</div>
                      <div className="fw-semibold small">{form.beneficiary_name}</div>
                      <div className="text-muted small">
                        {form.ben_bank} · {form.ben_acc}
                      </div>
                    </>
                  ) : (
                    "Enter beneficiary"
                  )}
                </div>
              </div>

              <div className="text-center mb-3">
                <div className="dt-summary-label">Amount to transfer</div>
                <div className="dt-summary-amount">
                  ₦{form.amount ? formatNumber(form.amount) : "0.00"}
                </div>
              </div>

              <ul className="dt-checklist">
                {checklist.map((item) => (
                  <li key={item.key} className={item.done ? "done" : ""}>
                    <span className="dt-check-icon">
                      {item.done ? <FontAwesomeIcon icon={faCheck} /> : ""}
                    </span>
                    {item.label}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="dt-send-btn mt-3"
                disabled={!formComplete || isLoading}
                onClick={() => {
                  if (validateForm()) openConfirmation();
                }}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Send money
              </button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        show={modalIsOpen1}
        onHide={closeConfirmation}
        centered
        className="dt-confirm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faLock} />
            Confirm transfer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dt-confirm-flow">
            <div className="dt-confirm-party from">
              <div className="dt-confirm-party-label">From</div>
              <div className="dt-confirm-party-name">{form.account_name}</div>
              <div className="dt-confirm-party-detail">
                {form.title} ({form.account_number})
              </div>
              <div className="dt-confirm-party-detail small mt-1">
                {form.type}
              </div>
            </div>
            <div className="dt-confirm-arrow">
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
            <div className="dt-confirm-party to">
              <div className="dt-confirm-party-label">To</div>
              <div className="dt-confirm-party-name">{form.beneficiary_name}</div>
              <div className="dt-confirm-party-detail">{form.ben_bank}</div>
              <div className="dt-confirm-party-detail">{form.ben_acc}</div>
            </div>
          </div>

          <div className="dt-confirm-amount">
            <div className="dt-confirm-amount-label">Transfer amount</div>
            <div className="dt-confirm-amount-value">
              ₦{formatNumber(form.amount)}
            </div>
            {form.description && (
              <div className="text-muted small mt-1">{form.description}</div>
            )}
          </div>

          <div className="dt-pin-section">
            <label htmlFor="dt-pin">
              <FontAwesomeIcon icon={faLock} className="me-1" />
              Enter your 4-digit PIN
            </label>
            <OtpInput
              value={pin}
              onChange={setPin}
              numInputs={4}
              renderInput={(props) => (
                <input {...props} type="password" inputMode="numeric" />
              )}
              shouldAutoFocus
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeConfirmation}
            disabled={loading}
          >
            Cancel
          </Button>
          <CustomButton
            loading={loading}
            disabled={pin.length < 4}
            onClick={processPayment}
          >
            Confirm &amp; pay
          </CustomButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DirectTransfer;
